import { useCallback } from 'react';
import { LooseObject } from '@gif/shared-global';
import {
  setBranchLink as mutateBranchLink,
  setBranchInviteToGroupData as mutateBranchInviteToGroupData,
  resetBranchLinkIsLoading,
  resetBranchDataIsLoading,
} from '@gif/shared-rtk';
import { useAppDispatch, useAppSelector, RootState } from '../store';

interface CreateBranchLinkParams {
  feature: string;
  gameTitle: string;
  gameId: string;
  groupId: string;
  gameLogo: string;
}

interface GameData {
  dataParsed?: LooseObject;
}

export const useBranch = () => {
  const data = useAppSelector((state: RootState) => state.branch);
  const branchLink = data.branchLink;
  const branchInviteToGroupData = data.branchData;
  const dispatch = useAppDispatch();
  const setBranchLink = useCallback(
    (link: string) => {
      dispatch(mutateBranchLink(link));
    },
    [dispatch]
  );
  const setBranchInviteToGroupData = useCallback(
    (gameData: GameData) => {
      dispatch(mutateBranchInviteToGroupData(gameData));
    },
    [dispatch]
  );
  const createBranchLink = useCallback(
    (params: CreateBranchLinkParams) => {
      resetBranchLinkIsLoading();
      const { feature, gameId, groupId, gameTitle, gameLogo } = params;
      if (!window.branch?.link || !feature || !gameId || !groupId) {
        setBranchLink('');
        return;
      }

      // deep links for mobile to navigate into the game
      const $deeplink_path = `mlbatbat://gaminghub?gameId=${gameId}&groupId=${groupId}`;
      const $canonical_url = `mlbatbat://gaminghub?gameId=${gameId}&groupId=${groupId}`;
      const $desktop_url = `${window.location.origin}/play`;
      const $og_title = `Join this ${gameTitle} Group`;
      const $og_description = 'MLB Play';

      window.branch?.link(
        {
          feature,
          data: {
            gameId,
            groupId,
            $deeplink_path,
            $canonical_url,
            $desktop_url,
            $og_title,
            $og_description,
            $og_image_url: gameLogo,
          },
        },
        (error, link) => {
          const isError = error;
          if (isError) {
            // TODO: Handle error with toast notification (or some other non-intrusive way)
            console.error('create branch link error', error);
          }
          setBranchLink(link || '');
        }
      );
    },
    [setBranchLink]
  );

  const loadInviteToGroupData = useCallback(() => {
    resetBranchDataIsLoading();
    if (!window.branch?.data) {
      setBranchInviteToGroupData({});
      return;
    }

    window.branch?.data((error, results) => {
      if (error) {
        // TODO: Handle error with toast notification (or some other non-intrusive way)
        console.error('get branch game error', error);
      }
      setBranchInviteToGroupData(results?.data_parsed || {});
    });
  }, [setBranchInviteToGroupData]);

  return {
    createBranchLink,
    branchLink,
    isBranchLinkLoading: data.isBranchLinkLoading,
    loadInviteToGroupData,
    branchInviteToGroupData,
    isBranchInviteToGroupDataLoading: data.isBranchDataLoading,
  };
};

export default useBranch;
